import * as tabbable from 'tabbable';
import * as _ from 'underscore';

import { Keys } from '../helpers/Keys';
import { ISize } from '../models/ISize';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { IPosition, IStackedElement, StackedElementsService } from './StackedElementsService';

/*
  ModalService is modelled on Bootstrap's $modal service: http://angular-ui.github.io/bootstrap/

  It has a single method: open(options). The options are:

    templateUrl (required) - a path to a template representing the modal's content
    scope - a scope instance to be used for the modal's content. Defaults to $rootScope.
    controller - a controller for a modal instance - it can initialize scope used by modal.
    resolve - members that will be resolved and passed to the controller as locals; it is equivalent
             of the resolve property for AngularJS routes.

  The open method returns a modal instance, an object with the following properties:

    close(result) - a method that can be used to close a modal, passing a result
    dismiss(reason) - a method that can be used to dismiss a modal, passing a reason
    result - a promise that is resolved when a modal is closed and rejected when a modal is dismissed

  In addition the scope associated with modal's content is augmented with 2 methods:

    close(result)
    dismiss(reason)

  These methods make it easy to close a modal window without the need to create a dedicated controller.

  Example:

    var modalInstance = modalService.open({
      templateUrl: 'myModalContent.html',
      controller: ModalInstanceCtrl,
      resolve: {
        items: function () {
          return [ 'item1', 'item2' ];
        }
      }
    });

    modalInstance.result.then(
      (closeResult: any) => {
        $log.info('Modal closed with result: ' + closeResult);
      },
      (dismissResult: any) => {
        $log.info('Modal dismissed with result: ' + dismissResult);
      });

*/

// Constants
const DistanceFromHostElement = 15;   // Distance between host element and modal border
const ArrowWidth = 29;                // Width of the base of the modal arrow
export const HorizontalWhiteSpace = 80;        // Each side of the horizontal white space is 40px wide.

export let ModalSize = {
  // Add other sizes as required
  medium: '400px',
  large: '600px',
  fitToContent: 'auto'
};

export enum ModalType {
  Normal,
  Unification
}

export interface IModalOptions {
  templateUrl: string;
  controller?: any;
  resolve?: {};
  scope?: ng.IScope;
  position?: IModalPositionOptions;
  keyboard?: boolean;       // indicates whether the dialog should be closable by hitting the ESC key, defaults to true
  classes?: string; // space-separated class names
  size?: string;    // See ModalSize for recommended values. Defaults to 'medium'.
  type?: ModalType; // Defaults to ModalType.Default
  title?: string;   // Provide if modal type is ModalType.Unification

  // an internal value that is used to indicate that the dialog is positioned relative to a position element, and the arrow should be shown
  _showArrow?: boolean;

  // Controls presence of a backdrop. Allowed values:
  //   true (default),
  //   false (no backdrop),
  //   'static' - backdrop is present but modal window is not closed when clicking outside of the modal window.
  backdrop?: true | false | 'static';
  backdropClasses?: string; // Space-separated class names

  // Toggles the presence of a close X icon in the modal corner
  //   true: show X (default)
  //   false: hide X
  closeX?: boolean;
  tbTestId?: string; // Defaults to 'modal'
}

export interface IModalPositionOptions {
  hostElement?: JQuery;
  reserveHeight?: number;
}

export interface IModalScope extends ng.IScope {
  close: (result: any) => void;
  dismiss: (reason?: any) => void;
  closeAndResolveAfterPromise: (result: any, promise: ng.IPromise<any>) => void;
  closeWithoutResolve: (result: any) => void;
  justResolve: (result: any) => void;

  options: IModalOptions;
}

export interface IModalInstance {
  close: (result: any) => void;
  dismiss: (reason?: any) => void;
  result: ng.IPromise<any>;
  _id?: string;
  _scope?: ng.IScope;
}

export interface IBackdropScope extends ng.IScope {
  backdropClick(): void;
  options: IModalOptions;
}

export interface IBackdrop {
  scope: IBackdropScope;
  element: JQuery;
}

/**
 * @deprecated This should be used only in legacy Angular code. Use ReactModalService instead.
 */
export class ModalService {

  public static $inject = ['$window', '$compile', '$rootScope', '$q',
    '$controller', '$injector', '$templateCache', '$http'];
  public static BackdropClass = 'tb-backdrop';
  public static MinimumSpaceBetweenDialogAndViewport = 18;

  private modalInstances: Set<IModalInstance>;
  private id = 0;

  constructor(
    private $window: ng.IWindowService,
    private $compile: ng.ICompileService,
    private $rootScope: ng.IScope,
    private $q: ng.IQService,
    private $controller: ng.IControllerService,
    private $injector: ng.auto.IInjectorService,
    private $templateCache: ng.ITemplateCacheService,
    private $http: ng.IHttpService,) {
    this.modalInstances = new Set<IModalInstance>((modalInstance: IModalInstance) => modalInstance._id);
  }

  private StackedElementsService = StackedElementsService.instance;
  /**
   * Creates an instance of IModalPositionOptions that will instruct the modal service to anchor
   * the dialog to the element.
   * @param element
   */
  public static anchoredTo(element: JQuery): IModalPositionOptions {
    return { hostElement: element };
  }

  /**
   * @deprecated This should be used only in legacy Angular code. Use ReactModalService.open instead.
   */
  public open(options: IModalOptions): IModalInstance {

    // Verify options
    if (!options.templateUrl) {
      throw new Error('templateUrl option is required.');
    }

    // Default values
    _.defaults(options, {
      size: ModalSize.medium,
      keyboard: true,
      resolve: {},
      scope: this.$rootScope,
      backdrop: true,
      closeX: true,
      tbTestId: 'modal',
      type: ModalType.Normal,
      title: ''
    });

    const deferredResult = this.$q.defer();

    let modalStackedElement: IStackedElement;
    let backdropStackedElement: IStackedElement;
    // Create modal scope
    const modalScope: IModalScope = <IModalScope>options.scope.$new();
    let backdrop: IBackdrop;

    // TODO: create a service called ModalStack and move open/close logic into there.
    //       see https://github.com/angular-ui/bootstrap/blob/master/src/modal/modal.js
    const modalInstance = {
      result: deferredResult.promise,
      close: (result: any) => {
        this.close(modalInstance, modalStackedElement, backdropStackedElement, backdrop);
        deferredResult.resolve(result);
      },
      closeAndResolveAfterPromise: async (result: any, promise: ng.IPromise<any>) => {
        this.closeWithoutDestoryingScope(modalInstance, modalStackedElement, backdropStackedElement);
        await promise;
        this.destroyScope(modalInstance, backdrop);
        deferredResult.resolve(result);
      },
      dismiss: (reason?: any) => {
        this.close(modalInstance, modalStackedElement, backdropStackedElement, backdrop);
        deferredResult.reject(reason);
      },
      _id: (this.id++).toString(),
      _scope: modalScope
    };

    this.modalInstances.add(modalInstance);

    const promises = this.getResolvePromises(options.resolve);

    // Pre-load the modal template to avoid flickering/re-layout
    promises.push(this.$http.get(options.templateUrl, { cache: this.$templateCache }));

    this.$q.all(promises).then((resolvedValues: any[]) => {

      const modalElement = $('<tb:modal />');

      // We add the modal type to the attributes of the element that invokes the tbModal directive, below, because
      // the directive needs to declare its templateUrl before the scope is available. So, we get the type from the
      // element attributes instead.
      modalElement.attr('modal-type', options.type);

      if (options.classes) {
        modalElement.addClass(options.classes);
      }

      backdrop = this.createBackdrop(modalInstance, options);

      modalScope.options = options;

      // Add close() and dismiss() convenience methods to the scope.
      modalScope.close = modalInstance.close;
      modalScope.dismiss = modalInstance.dismiss;
      modalScope.closeAndResolveAfterPromise = modalInstance.closeAndResolveAfterPromise;

      // Controller
      if (options.controller) {
        const ctrlLocals: any = {
          $scope: modalScope,
          modalInstance: modalInstance
        };
        let resolveIter = 0;
        angular.forEach(options.resolve, (value: any, key: string) => {
          ctrlLocals[key] = resolvedValues[resolveIter++];
        });
        this.$controller(options.controller, ctrlLocals);
      }

      this.$compile(modalElement)(modalScope);

      const fillWindow = () => { return { left: 0, top: 0, right: 0, bottom: 0 }; };
      backdropStackedElement = this.StackedElementsService.addStackedElement({
        contentElement: backdrop.element,
        getContentElementPosition: fillWindow
      });

      // Broadcast changes in modal position
      let lastPosition: IPosition = undefined;
      modalStackedElement = this.StackedElementsService.addStackedElement({
        contentElement: modalElement,
        getContentElementPosition: () => {
          const newPosition = this.position(modalElement, options);
          if (!_.isEqual(newPosition, lastPosition)) {
            modalScope.$broadcast('positionChanged', newPosition);
            lastPosition = newPosition;
          }
          return newPosition;
        }
      });

      this.attachKeydownHandler(modalInstance, options);
    });

    return modalInstance;
  }

  private createBackdrop(modalInstance: IModalInstance, options: IModalOptions): IBackdrop {
    const backdropElement = $('<div ng-click="backdropClick()" ng-show="options.backdrop" />').addClass(ModalService.BackdropClass);
    const backdropScope = <IBackdropScope>this.$rootScope.$new();
    // put the entire options object on the scope so we don't have to setup a binding for options.backdrop changing
    backdropScope.options = options;
    if (backdropScope.options.backdropClasses) {
      backdropElement.addClass(backdropScope.options.backdropClasses);
    }
    backdropScope.backdropClick = () => {
      if (options.backdrop && options.backdrop !== 'static') {
        modalInstance.dismiss('backdrop click');
      }
    };
    this.$compile(backdropElement)(backdropScope);
    return {
      element: backdropElement,
      scope: backdropScope
    };
  }

  private attachKeydownHandler(modalInstance: IModalInstance, options: IModalOptions) {
    $(this.$window.document).on('keydown.modal' + modalInstance._id, ($event: JQuery.Event) => {
      if ($event.which === Keys.Escape && options.keyboard) {
        this.$rootScope.$apply(() => modalInstance.dismiss('esc key press'));
      }
    });
  }

  private detachKeydownHandler(modalInstance: IModalInstance) {
    $(this.$window.document).off('.modal' + modalInstance._id);
  }

  private getWindowSize(): ISize {
    const _window = $(this.$window);
    return {
      width: _window.width(),
      height: _window.height()
    };
  }

  private position(modalElement: JQuery, options: IModalOptions): IPosition {
    const minimumSpace = ModalService.MinimumSpaceBetweenDialogAndViewport;
    const reserveHeight = Math.max(modalElement.outerHeight(), (options.position && options.position.reserveHeight) || 0);

    let position = this.positionRelativeToHostElement(modalElement, options, reserveHeight);
    if (!position) {
      // if relative positioning failed, fall back to using centered positioning.
      const _window = this.getWindowSize();
      options._showArrow = false;
      position = {
        left: _window.width / 2 - modalElement.outerWidth() / 2,

        // keep the top of the modal from being positioned above the viewport
        top: Math.max(_window.height / 2 - reserveHeight / 2, minimumSpace)
      };
      if (position.top + reserveHeight + minimumSpace > _window.height) {
        position.top = minimumSpace;
        position.bottom = minimumSpace;
      }
    }
    return position;
  }

  /**
  * Attempts to position the modal element relative to the host element.
  * First it will attempt to place the modal below the host element.
  * Next it will attempt to place the modal above the host element.
  * If both of these attempts fail, it will return null to signal that positioning failed.
  */
  private positionRelativeToHostElement(modalElement: JQuery, options: IModalOptions, reserveHeight: number): IPosition {
    // Make sure we have a host element and it is in the DOM
    if (!options.position || !options.position.hostElement || options.position.hostElement.parents('body').length === 0) {
      return null;
    }

    const minimumSpace = ModalService.MinimumSpaceBetweenDialogAndViewport;
    const _window = this.getWindowSize();

    const hostElement = options.position.hostElement;
    const hostElementOffset = hostElement.offset();

    // By default, align the dialog's center with the host element's center, and place the dialog beneath
    // the host element.
    let left = hostElementOffset.left + hostElement.outerWidth() / 2 - modalElement.outerWidth() / 2;
    let top = hostElementOffset.top + hostElement.outerHeight() + DistanceFromHostElement;

    // Keep the dialog inside the window: horizontally
    left = Math.min(left, _window.width - minimumSpace - modalElement.outerWidth());
    left = Math.max(left, minimumSpace);

    // Keep the dialog inside the window: vertically
    if (top + reserveHeight > _window.height - minimumSpace) {
      const spaceAboveHostElement = hostElementOffset.top - DistanceFromHostElement - reserveHeight;
      if (spaceAboveHostElement >= minimumSpace) {
        // Use actual height instead of reserve height when positioning above the host element.
        // Otherwise, the element will be positioned too far above the position element since we are assuming that it
        // is larger than it actually is.
        top = hostElementOffset.top - modalElement.outerHeight() - DistanceFromHostElement;
      } else {
        // the dialog is too tall to fit above or below the position element.
        return null;
      }
    }

    options._showArrow = true;
    return {
      left: left,
      top: top
    };
  }

  private getResolvePromises(resolves: {}): ng.IPromise<any>[] {
    const promisesArr: ng.IPromise<any>[] = [];
    angular.forEach(resolves, (value: any) => {
      if (angular.isFunction(value) || angular.isArray(value)) {
        promisesArr.push(this.$q.when(this.$injector.invoke(value)));
      }
    });
    return promisesArr;
  }

  private close(modalInstance: IModalInstance, modalStackedElement: IStackedElement, backdropStackedElement: IStackedElement, backdrop: IBackdrop) {
    this.detachKeydownHandler(modalInstance);
    modalInstance._scope.$destroy();
    this.StackedElementsService.removeStackedElement(modalStackedElement);
    backdrop && backdrop.scope && backdrop.scope.$destroy();
    this.StackedElementsService.removeStackedElement(backdropStackedElement);
    this.modalInstances.remove(modalInstance);
  }

  private closeWithoutDestoryingScope(modalInstance: IModalInstance, modalStackedElement: IStackedElement, backdropStackedElement: IStackedElement) {
    this.detachKeydownHandler(modalInstance);
    this.StackedElementsService.removeStackedElement(modalStackedElement);
    this.StackedElementsService.removeStackedElement(backdropStackedElement);
  }

  private destroyScope(modalInstance: IModalInstance, backdrop: IBackdrop) {
    modalInstance._scope.$destroy();
    backdrop && backdrop.scope && backdrop.scope.$destroy();
    this.modalInstances.remove(modalInstance);
  }

  /**
   * @deprecated This should be used to dismiss dialogs created with ModalService.
   * ReactModalService.dismissAll will be used to dismiss dialogs created with ReactModalService.
   */
  public dismissAll(reason?: any) {
    _.each(this.modalInstances.toArray(), (modalInstance: IModalInstance) => {
      modalInstance.dismiss(reason);
      this.modalInstances.remove(modalInstance);
    });
  }

}

Module.instance().service('ModalService', ModalService);

interface IModalHolderScope extends IModalScope {
  showArrow(): boolean;
  arrowUp: boolean;
  arrowLeft: number;
}

Module.instance().directive('tbModal', [
  '$window', '$timeout',
  ($window: ng.IWindowService, $timeout: ng.ITimeoutService) => {

    function getTabbableElementsInModal(element: JQuery): { first: HTMLElement, last: HTMLElement } {
      const tabbables = tabbable(element.get(0));
      const first = tabbables.length > 0 ? tabbables[0] : null;
      const last = tabbables.length > 0 ? tabbables[tabbables.length - 1] : null;
      return { first, last };
    }

    return {
      restrict: 'E',
      replace: true,
      templateUrl: (elem: any, attr: any) => (parseInt(attr.modalType, 10) === ModalType.Unification ? 'modal-unification.html' : 'modal.html'),
      link: (scope: IModalHolderScope, element: JQuery) => {

        scope.showArrow = () => !!scope.options._showArrow;
        scope.arrowUp = true;
        scope.arrowLeft = 0;
        let modalOffsetTop = 0;

        // Keep focus inside the dialog
        // Adapted from code at https://github.com/fnagel/jQuery-Accessible-RIA/issues/30
        element.on('keydown', ($event: JQuery.TriggeredEvent) => {
          if ($event.which !== Keys.Tab) {
            return;
          }

          const { first, last } = getTabbableElementsInModal(element);
          if ($event.target === last && !$event.shiftKey) {
            $event.preventDefault();
            first.focus();
          } else if ($event.target === first && $event.shiftKey) {
            $event.preventDefault();
            last.focus();
          }
        });

        // Upon opening the modal, focus the first element.
        // For some reason, we have to do it asynchronously or else it doesn't find the elements.
        $timeout(() => {
          const { first } = getTabbableElementsInModal(element);
          if (first) {
            first.focus();
          }
        });

        scope.$on('positionChanged', (event: ng.IAngularEvent, modalPosition: IPosition) => {
          if (scope.options.position) {
            const position = scope.options.position;
            const hostElement = position.hostElement;
            if (modalPosition && hostElement) {

              const hostElementPosition = hostElement.offset();
              scope.arrowUp = modalPosition.top > hostElementPosition.top;
              modalOffsetTop = modalPosition.top;
              const midpointOfHostElement = hostElementPosition.left + hostElement.outerWidth() / 2;
              scope.arrowLeft = midpointOfHostElement - (ArrowWidth / 2) - modalPosition.left;
            }
          }
        });

        element.css('width', scope.options.size);
        element.css('max-width', (document.body.offsetWidth - HorizontalWhiteSpace) + 'px');
      }
    };
  }
]);
